<script setup lang="ts">
import api from "@/api";
import { useToast } from "primevue/usetoast";

definePage({
	name: "AdminPanelPeople",
});

type TAdminPerson = {
	id: number | string;
	imageUrl: string;
	isGloballyKnown: boolean;
	name: string;
	raw: any;
};

const toast = useToast();

const people = ref<TAdminPerson[]>([]);
const page = ref(1);
const search = ref("");
const limit = 100;
const isLoading = ref(false);
const isLoadingMore = ref(false);
const hasMore = ref(true);
const updatingIds = ref(new Set<number | string>());
let searchTimeout: ReturnType<typeof setTimeout> | null = null;

function normalizeBoolean(raw: unknown): boolean {
	if (typeof raw === "boolean") return raw;
	if (typeof raw === "number") return raw === 1;
	if (typeof raw === "string") return raw === "1" || raw === "true";

	return false;
}

function getFirstArray(...values: any[]): any[] {
	for (const value of values) {
		if (Array.isArray(value)) {
			return value;
		}
	}

	return [];
}

function getImageUrl(raw: any) {
	const path =
		raw?.image_url ??
		raw?.profile_image_url ??
		raw?.photo_url ??
		raw?.profile_path ??
		raw?.poster_path ??
		raw?.ppl_image_url ??
		raw?.ppl_profile_path ??
		"";

	if (!path) return "";
	if (/^(https?:)?\/\//.test(path) || path.startsWith("data:")) return path;

	return `https://image.tmdb.org/t/p/original${path}`;
}

function normalizePerson(raw: any): TAdminPerson | null {
	const id = raw?.id ?? raw?.person_id ?? raw?.people_id ?? raw?.ppl_id;

	if (id === null || id === undefined || id === "") {
		return null;
	}

	return {
		id,
		imageUrl: getImageUrl(raw),
		isGloballyKnown: normalizeBoolean(
			raw?.isGloballyKnown ??
				raw?.is_globally_known ??
				raw?.is_globaly_known ??
				raw?.ppl_is_globally_known ??
				raw?.ppl_is_globaly_known,
		),
		name:
			String(raw?.name ?? raw?.title ?? raw?.ppl_name ?? raw?.person_name ?? "")
				.trim() || "Unknown person",
		raw,
	};
}

function getPeopleFromResponse(payload: any) {
	const root = payload?.data?.data ?? payload?.data?.res ?? payload?.data ?? payload;

	return getFirstArray(
		root?.people,
		root?.items,
		root?.rows,
		root?.data,
		root,
	)
		.map(normalizePerson)
		.filter((person): person is TAdminPerson => Boolean(person));
}

async function loadPeople(reset = false) {
	if (isLoading.value || isLoadingMore.value) {
		return;
	}

	if (reset) {
		page.value = 1;
		hasMore.value = true;
		people.value = [];
	}

	if (!hasMore.value) {
		return;
	}

	try {
		if (page.value === 1) {
			isLoading.value = true;
		} else {
			isLoadingMore.value = true;
		}

		const res = await api.getAdminPanelPeople({
			page: page.value,
			limit,
			search: search.value.trim() || null,
		});

		const nextPeople = getPeopleFromResponse(res);
		const existingIds = new Set(people.value.map((person) => person.id));
		const uniquePeople = nextPeople.filter((person) => !existingIds.has(person.id));

		people.value = reset ? nextPeople : [...people.value, ...uniquePeople];
		hasMore.value = nextPeople.length >= limit;
		page.value += 1;
	} catch (err: any) {
		console.warn(err.message);
		toast.add({
			severity: "error",
			summary: "Error",
			detail: "Unable to load people.",
			life: 3000,
		});
	} finally {
		isLoading.value = false;
		isLoadingMore.value = false;
	}
}

async function toggleGloballyKnown(person: TAdminPerson, checked: boolean) {
	const previousValue = person.isGloballyKnown;
	const nextUpdatingIds = new Set(updatingIds.value);
	nextUpdatingIds.add(person.id);
	updatingIds.value = nextUpdatingIds;
	person.isGloballyKnown = checked;

	try {
		await api.editAdminPanelPeople({
			peopleIds: [person.id],
			isGloballyKnown: checked ? 1 : 0,
		});
	} catch (err: any) {
		console.warn(err.message);
		person.isGloballyKnown = previousValue;
		toast.add({
			severity: "error",
			summary: "Error",
			detail: "Unable to update person.",
			life: 3000,
		});
	} finally {
		const updatedUpdatingIds = new Set(updatingIds.value);
		updatedUpdatingIds.delete(person.id);
		updatingIds.value = updatedUpdatingIds;
	}
}

function isUpdating(person: TAdminPerson) {
	return updatingIds.value.has(person.id);
}

function queueSearch() {
	if (searchTimeout) {
		clearTimeout(searchTimeout);
	}

	searchTimeout = setTimeout(() => {
		loadPeople(true);
	}, 350);
}

onMounted(() => {
	loadPeople(true);
});

onBeforeUnmount(() => {
	if (searchTimeout) {
		clearTimeout(searchTimeout);
	}
});
</script>

<template>
	<section class="admin-people-page">
		<header class="admin-people-header">
			<div>
				<p class="admin-people-eyebrow">Temporary Admin</p>
				<h1 class="admin-people-title">People</h1>
			</div>

			<button
				type="button"
				class="admin-people-button"
				:disabled="isLoading"
				@click="loadPeople(true)"
			>
				{{ isLoading ? "Loading..." : "Refresh" }}
			</button>
		</header>

		<form
			class="admin-people-search"
			@submit.prevent="loadPeople(true)"
		>
			<input
				v-model="search"
				type="search"
				class="admin-people-search__input"
				placeholder="Search people..."
				@input="queueSearch"
			/>

			<button
				type="submit"
				class="admin-people-button"
				:disabled="isLoading"
			>
				Search
			</button>
		</form>

		<div
			v-if="isLoading && !people.length"
			class="admin-people-state"
		>
			Loading people...
		</div>

		<div
			v-else-if="!people.length"
			class="admin-people-state"
		>
			No people found.
		</div>

		<div
			v-else
			class="admin-people-list"
		>
			<article
				v-for="person in people"
				:key="person.id"
				class="admin-person-row"
			>
				<div class="admin-person-image-wrap">
					<img
						v-if="person.imageUrl"
						:src="person.imageUrl"
						:alt="person.name"
						class="admin-person-image"
						loading="lazy"
					/>
					<div
						v-else
						class="admin-person-image admin-person-image--empty"
					>
						{{ person.name.charAt(0) }}
					</div>
				</div>

				<div class="admin-person-main">
					<h2 class="admin-person-name">{{ person.name }}</h2>
					<p class="admin-person-id">ID: {{ person.id }}</p>
				</div>

				<label class="admin-person-toggle">
					<input
						:checked="person.isGloballyKnown"
						type="checkbox"
						:disabled="isUpdating(person)"
						@change="
							toggleGloballyKnown(
								person,
								($event.target as HTMLInputElement).checked,
							)
						"
					/>
					<span>is globally known</span>
				</label>
			</article>
		</div>

		<div
			v-if="people.length"
			class="admin-people-footer"
		>
			<button
				type="button"
				class="admin-people-button"
				:disabled="isLoadingMore || !hasMore"
				@click="loadPeople()"
			>
				{{
					hasMore
						? isLoadingMore
							? "Loading..."
							: "Load more"
						: "Everything loaded"
				}}
			</button>
		</div>
	</section>
</template>

<style scoped lang="scss">
.admin-people-page {
	height: 100dvh;
	min-height: 0;
	overflow-y: auto;
	overflow-x: hidden;
	-webkit-overflow-scrolling: touch;
	padding: 24px;
	color: #ffffff;
}

.admin-people-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	margin-bottom: 20px;
}

.admin-people-eyebrow {
	margin: 0 0 4px;
	color: rgba(255, 255, 255, 0.62);
	font-size: 12px;
	font-weight: 800;
	text-transform: uppercase;
}

.admin-people-title {
	margin: 0;
	font-size: clamp(30px, 5vw, 48px);
	font-weight: 900;
	line-height: 1;
}

.admin-people-button {
	border: 1px solid rgba(255, 255, 255, 0.14);
	border-radius: 999px;
	padding: 10px 14px;
	background: rgba(255, 255, 255, 0.12);
	color: #ffffff;
	font-weight: 800;
	cursor: pointer;
}

.admin-people-button:disabled {
	cursor: default;
	opacity: 0.55;
}

.admin-people-search {
	display: grid;
	grid-template-columns: minmax(0, 1fr) auto;
	gap: 10px;
	margin-bottom: 16px;
}

.admin-people-search__input {
	width: 100%;
	min-height: 42px;
	border: 1px solid rgba(255, 255, 255, 0.14);
	border-radius: 999px;
	padding: 0 14px;
	background: rgba(255, 255, 255, 0.08);
	color: #ffffff;
	font-size: 14px;
	outline: 0;
}

.admin-people-search__input::placeholder {
	color: rgba(255, 255, 255, 0.46);
}

.admin-people-state {
	padding: 18px;
	border-radius: 8px;
	background: rgba(255, 255, 255, 0.08);
	color: rgba(255, 255, 255, 0.74);
}

.admin-people-list {
	display: flex;
	flex-direction: column;
	gap: 10px;
}

.admin-person-row {
	display: grid;
	grid-template-columns: 72px minmax(0, 1fr) auto;
	align-items: center;
	gap: 14px;
	padding: 12px;
	border: 1px solid rgba(255, 255, 255, 0.1);
	border-radius: 8px;
	background: rgba(255, 255, 255, 0.07);
}

.admin-person-image-wrap,
.admin-person-image {
	width: 72px;
	height: 96px;
}

.admin-person-image {
	display: grid;
	place-items: center;
	border-radius: 6px;
	object-fit: cover;
	background: rgba(255, 255, 255, 0.08);
	color: rgba(255, 255, 255, 0.72);
	font-size: 26px;
	font-weight: 900;
}

.admin-person-main {
	min-width: 0;
}

.admin-person-name {
	margin: 0;
	font-size: 18px;
	font-weight: 900;
}

.admin-person-id {
	margin: 5px 0 0;
	color: rgba(255, 255, 255, 0.55);
	font-size: 12px;
}

.admin-person-toggle {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	color: rgba(255, 255, 255, 0.82);
	font-size: 13px;
	font-weight: 800;
	white-space: nowrap;
	cursor: pointer;
}

.admin-person-toggle input {
	width: 18px;
	height: 18px;
	accent-color: #ffffff;
}

.admin-people-footer {
	display: flex;
	justify-content: center;
	padding: 20px 0 0;
}

@media (max-width: 640px) {
	.admin-people-page {
		padding: 16px;
	}

	.admin-people-header,
	.admin-person-row {
		align-items: flex-start;
		grid-template-columns: 64px minmax(0, 1fr);
	}

	.admin-people-header {
		flex-direction: column;
	}

	.admin-people-search {
		grid-template-columns: 1fr;
	}

	.admin-person-image-wrap,
	.admin-person-image {
		width: 64px;
		height: 86px;
	}

	.admin-person-toggle {
		grid-column: 2;
	}
}
</style>
